import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiError } from '../../api';
import PageHeader from '../../components/PageHeader';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Inbox, Mail, MailOpen, AlertTriangle, Send, ArrowUpRight, CheckCircle2, XCircle, Ban, Clock } from 'lucide-react';

const TABS = [
    { key: 'all', label: 'All', icon: Inbox },
    { key: 'unread', label: 'Unread', icon: Mail },
    { key: 'replies', label: 'Replies', icon: MailOpen },
    { key: 'needs_action', label: 'Needs Action', icon: AlertTriangle },
    { key: 'sent', label: 'Sent', icon: Send },
    { key: 'bounced', label: 'Bounced', icon: XCircle },
];

export default function LeadGenInbox() {
    const navigate = useNavigate();
    const [tab, setTab] = useState('all');
    const [items, setItems] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [replyText, setReplyText] = useState('');
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get('/leadgen/inbox', { params: { tab } });
            setItems(r.data.items);
            setUnreadCount(r.data.unread_count);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
        finally { setLoading(false); }
    }, [tab]);
    useEffect(() => { load(); setSelected(null); }, [load]);

    const open = async (item) => {
        setSelected(item);
        setReplyText('');
        if (item.kind !== 'sent' && !item.is_read) {
            try { await api.post(`/leadgen/inbox/${item.id}/read`); load(); } catch { /* noop */ }
        }
    };

    const act = async (path, successMsg) => {
        setBusy(true);
        try {
            await api.post(path);
            toast.success(successMsg);
            load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    const sendReply = async () => {
        if (!replyText.trim() || !selected) return;
        setBusy(true);
        try {
            await api.post(`/leadgen/inbox/${selected.id}/reply`, { body_html: `<p>${replyText.replace(/\n/g, '<br/>')}</p>` });
            toast.success('Reply sent — stays in the same thread');
            setReplyText('');
            load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    return (
        <div data-testid="leadgen-inbox-page" className="bm-fade-in">
            <PageHeader title="Inbox" description="Replies and outreach activity from your Email Campaigns, matched to Companies & Contacts." />
            <div className="px-8 py-6">
                <div className="flex items-center gap-1 mb-4 flex-wrap">
                    {TABS.map(t => {
                        const Icon = t.icon;
                        return (
                            <button key={t.key} onClick={() => setTab(t.key)}
                                    className={`flex items-center gap-1.5 text-[12.5px] font-medium px-3 py-1.5 rounded-md border ${tab === t.key ? 'bg-[color:var(--bm-primary)] text-white border-transparent' : 'bg-white border-[color:var(--bm-border)] hover:bg-[color:var(--bm-bg-mute)]'}`}
                                    data-testid={`inbox-tab-${t.key}`}>
                                <Icon size={13} /> {t.label} {t.key === 'unread' && unreadCount > 0 && <span className="ml-0.5">({unreadCount})</span>}
                            </button>
                        );
                    })}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-5">
                    <div className="bg-white rounded-xl border border-[color:var(--bm-border)] overflow-hidden max-h-[65vh] overflow-y-auto" data-testid="inbox-list">
                        {loading && <div className="px-5 py-8 text-center text-[color:var(--bm-text-muted)]">Loading…</div>}
                        {!loading && items.length === 0 && <div className="px-5 py-10 text-center text-[13px] text-[color:var(--bm-text-muted)]">Nothing here.</div>}
                        {items.map(it => (
                            <button key={it.id} onClick={() => open(it)}
                                    className={`w-full text-left px-4 py-3 border-b border-[color:var(--bm-border)] hover:bg-[color:var(--bm-bg-mute)] ${selected?.id === it.id ? 'bg-[color:var(--bm-accent-50)]' : ''} ${!it.is_read ? 'font-semibold' : ''}`}
                                    data-testid={`inbox-item-${it.id}`}>
                                <div className="flex items-center justify-between gap-2">
                                    <span className="truncate text-[13px]">{it.contact_name || it.from_email || '—'} <span className="text-[color:var(--bm-text-subtle)] font-normal">· {it.company_name}</span></span>
                                    <KindBadge kind={it.kind} />
                                </div>
                                <div className="text-[12px] text-[color:var(--bm-text-muted)] truncate mt-0.5">{it.subject || '(no subject)'}</div>
                                <div className="text-[11px] text-[color:var(--bm-text-subtle)] mt-0.5">{it.received_at ? new Date(it.received_at).toLocaleString('en-GB') : ''}</div>
                            </button>
                        ))}
                    </div>

                    <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-5" data-testid="inbox-detail">
                        {!selected ? (
                            <div className="text-center text-[13px] text-[color:var(--bm-text-muted)] py-16">Select an item to view details</div>
                        ) : (
                            <div>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="font-display text-[16px] font-semibold">{selected.subject || '(no subject)'}</div>
                                        <div className="text-[12.5px] text-[color:var(--bm-text-muted)] mt-0.5">{selected.contact_name || selected.from_email} · {selected.company_name}</div>
                                    </div>
                                    <KindBadge kind={selected.kind} />
                                </div>
                                <div className="mt-4 text-[13px] text-[color:var(--bm-text)] whitespace-pre-wrap bg-[color:var(--bm-bg-mute)] rounded-lg p-4">
                                    {selected.body_preview || <span className="text-[color:var(--bm-text-subtle)] italic">No preview available</span>}
                                </div>

                                <div className="mt-4 flex items-center gap-2 flex-wrap">
                                    <Button size="sm" variant="outline" onClick={() => navigate(`/clients/${selected.client_id}`)} data-testid="inbox-view-company-btn">
                                        <ArrowUpRight size={14} className="mr-1" /> View company
                                    </Button>
                                    {selected.kind !== 'sent' && !selected.is_read && (
                                        <Button size="sm" variant="outline" onClick={() => act(`/leadgen/inbox/${selected.id}/unread`, 'Marked unread')} disabled={busy}>Mark unread</Button>
                                    )}
                                    <Button size="sm" variant="outline" onClick={() => act(`/leadgen/inbox/contacts/${selected.contact_id}/follow-up`, 'Follow-up scheduled')} disabled={busy} data-testid="inbox-follow-up-btn">
                                        <Clock size={14} className="mr-1" /> Follow-up
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => act(`/leadgen/inbox/contacts/${selected.contact_id}/interested`, 'Marked interested')} disabled={busy} className="text-emerald-700 border-emerald-200" data-testid="inbox-interested-btn">
                                        <CheckCircle2 size={14} className="mr-1" /> Interested
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => act(`/leadgen/inbox/contacts/${selected.contact_id}/not-interested`, 'Marked not interested')} disabled={busy} data-testid="inbox-not-interested-btn">
                                        <XCircle size={14} className="mr-1" /> Not interested
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => act(`/clients/${selected.client_id}/promote`, 'Promoted to Active Lead')} disabled={busy} className="text-teal-700 border-teal-200" data-testid="inbox-promote-btn">
                                        Promote to Active Lead
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => window.confirm('Suppress this contact permanently?') && act(`/leadgen/inbox/contacts/${selected.contact_id}/do-not-contact`, 'Added to suppression list')} disabled={busy} className="text-red-600 border-red-200" data-testid="inbox-dnc-btn">
                                        <Ban size={14} className="mr-1" /> Do Not Contact
                                    </Button>
                                </div>

                                {selected.kind === 'reply' && selected.ms_message_id && (
                                    <div className="mt-5 pt-5 border-t border-[color:var(--bm-border)]">
                                        <div className="text-[12px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold mb-2">Reply (stays in the same thread)</div>
                                        <Textarea value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Write your reply…" className="min-h-[100px]" data-testid="inbox-reply-textarea" />
                                        <Button onClick={sendReply} disabled={busy || !replyText.trim()} className="mt-2 h-9" style={{ background: 'var(--bm-primary)' }} data-testid="inbox-send-reply-btn">
                                            <Send size={14} className="mr-1.5" /> Send reply
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function KindBadge({ kind }) {
    const map = {
        reply: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        bounce: 'bg-red-50 text-red-700 border-red-200',
        sent: 'bg-slate-50 text-slate-600 border-slate-200',
    };
    return <span className={`text-[9.5px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border shrink-0 ${map[kind] || map.sent}`}>{kind}</span>;
}
